import { InterventionStatus } from "../../../components/patient/Patient-Menu/Interventions/data";
import { defaultInterventionSteps } from "../../../components/patient/text/data";

// defaultInterventionSteps is sourced from components/patient/text/data.jsx

// Compute step enablement purely from status
// - Active: enable first five; disable Documentation and Billing
// - Any other status (WaitingProviderResponse, Completed, Cancelled, etc.): enable only Documentation and Billing
const computeInterventionSteps = (intervention) => {
  const status = intervention?.status;
  return defaultInterventionSteps.map((step) => {
    if (status === InterventionStatus.Active || !status) {
      if (step.name === "Documentation" || step.name === "Billing") {
        return { ...step, disabled: true };
      }
      return { ...step, disabled: false };
    }

    if (step.name === "Documentation" || step.name === "Billing") {
      return { ...step, disabled: false };
    }
    return { ...step, disabled: true };
  });
};

const initialState = {
  interventions: [],
  currentIntervention: null,
  clinicalServiceObj: null,
  loading: false,
  error: null,
  fetchByIdFailedForId: null,
  searchFilters: {
    interventionType: null,
    clinicalServiceType: null,
    clinicalServiceTypeCode: null,
    createdDateFrom: null,
    createdDateTo: null
  }
};

const interventionReducer = (state = initialState, action) => {
  switch (action.type) {
    // Create Intervention
    case "CREATE_INTERVENTION_SUCCESS":
      return {
        ...state,
        loading: false,
        interventions: [...state.interventions, action.payload],
        currentIntervention: action.payload,
        error: null
      };
    case "CREATE_INTERVENTION_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Update Intervention
    case "UPDATE_INTERVENTION_SUCCESS":
      return {
        ...state,
        loading: false,
        interventions: state.interventions.map(intervention =>
          intervention.id === action.payload.id ? action.payload : intervention
        ),
        currentIntervention: action.payload,
        error: null
      };
    case "UPDATE_INTERVENTION_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Fetch Interventions
    case "FETCH_INTERVENTIONS_SUCCESS":
      return {
        ...state,
        loading: false,
        interventions: (action.payload || []).map((i) => ({
          ...i,
          interventionSteps: computeInterventionSteps(i)
        })),
        error: null
      };
    case "FETCH_INTERVENTIONS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Fetch Intervention by ID
    case "FETCH_INTERVENTION_BY_ID_SUCCESS":
      return {
        ...state,
        loading: false,
        fetchByIdFailedForId: null,
        currentIntervention: {
          ...action.payload,
          interventionSteps: computeInterventionSteps(action.payload)
        },
        error: null
      };
    case "FETCH_INTERVENTION_BY_ID_FAILURE":
      return {
        ...state,
        loading: false,
        currentIntervention: null,
        fetchByIdFailedForId: action?.payload?.interventionId ?? null,
        error: action?.payload?.errorMessage ?? action.payload ?? null
      };

    // Update Status
    case "UPDATE_INTERVENTION_STATUS_SUCCESS":
      return {
        ...state,
        loading: false,
        interventions: state.interventions.map(intervention =>
          intervention.id === action.payload.id
            ? { ...intervention, status: action.payload.status, interventionSteps: computeInterventionSteps({ ...intervention, status: action.payload.status }) }
            : intervention
        ),
        currentIntervention: state.currentIntervention?.id === action.payload.id 
          ? { ...state.currentIntervention, status: action.payload.status, interventionSteps: computeInterventionSteps({ ...state.currentIntervention, status: action.payload.status }) }
          : state.currentIntervention,
        error: null
      };
    case "UPDATE_INTERVENTION_STATUS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Update Assessment
    case "UPDATE_INTERVENTION_ASSESSMENT_SUCCESS":
      // The API returns only the assessment form data, not the full intervention
      // We include the interventionId in the payload to identify which intervention to update
      const { interventionId, ...assessmentData } = action.payload;
      
      return {
        ...state,
        loading: false,
        interventions: state.interventions.map(intervention =>
          intervention.id === interventionId ? {
            ...intervention,
            interventionAssessment: {
              ...intervention.interventionAssessment,
              interventionAssessmentForm: assessmentData
            },
            // Recompute steps based on updated assessment (e.g., notification added)
            interventionSteps: computeInterventionSteps({
              ...intervention,
              interventionAssessment: {
                ...intervention.interventionAssessment,
                interventionAssessmentForm: assessmentData
              }
            })
          } : intervention
        ),
        currentIntervention: state.currentIntervention?.id === interventionId ? {
          ...state.currentIntervention,
          interventionAssessment: {
            ...state.currentIntervention.interventionAssessment,
            interventionAssessmentForm: assessmentData
          },
          interventionSteps: computeInterventionSteps({
            ...state.currentIntervention,
            interventionAssessment: {
              ...state.currentIntervention.interventionAssessment,
              interventionAssessmentForm: assessmentData
            }
          })
        } : state.currentIntervention,
        error: null
      };
    case "UPDATE_INTERVENTION_ASSESSMENT_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Search Filters
    case "SET_SEARCH_FILTERS":
      return {
        ...state,
        searchFilters: { ...state.searchFilters, ...action.payload }
      };
    case "CLEAR_SEARCH_FILTERS":
      return {
        ...state,
        searchFilters: {
          interventionType: null,
          clinicalServiceType: null,
          clinicalServiceTypeCode: null,
          createdDateFrom: null,
          createdDateTo: null
        }
      };

    // General
    case "CLEAR_CURRENT_INTERVENTION":
      return {
        ...state,
        currentIntervention: null,
        fetchByIdFailedForId: null,
        error: null
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: null
      };
    case "SET_LOADING":
      return {
        ...state,
        loading: action.payload
      };

    // Fetch Clinical Service for Intervention
    case "FETCH_CLINICAL_SERVICE_FOR_INTERVENTION_SUCCESS":
      return {
        ...state,
        clinicalServiceObj: action.payload,
        loading: false,
        error: null
      };

    case "FETCH_CLINICAL_SERVICE_FOR_INTERVENTION_FAILURE":
      return {
        ...state,
        clinicalServiceObj: null,
        loading: false,
        error: action.payload
      };

    case "RESET_CLINICAL_SERVICE_FOR_INTERVENTION":
      return {
        ...state,
        clinicalServiceObj: null,
        loading: false,
        error: null
      };

    default:
      return state;
  }
};

export default interventionReducer;
